import { convertToReportEvaluation } from 'actions/evaluations/EvaluationsConverter';
import { convertToIntakeFormAnswerDto } from 'actions/intakeForm/IntakeFormConverter';
import { PetDto, PetVaccineDto, SavePetDto, SavePetVaccineDto } from 'actions/pets/PetsDtos';
import moment from 'moment-timezone';
import { store } from 'store';
import { FiltrableOption, Option } from 'types/Generics';
import { Marketplace } from 'types/Marketplace';
import {
    Pet,
    PetBreed,
    PetHairLength,
    PetType,
    PetTypeId,
    PetTypeIds,
    PetVaccine,
    SavePet
} from 'types/Pet';
import { v4 as uuid } from 'uuid';

const findExoticBreed = (
    marketplace: Marketplace,
    exoticType: string | null
): PetBreed | undefined => {
    if (!exoticType) return undefined;

    const exotics = marketplace.petTypes[PetTypeIds.EXOTIC];
    const fromTypes = exotics.types?.find(type => type.id === exoticType);
    if (fromTypes) {
        return { id: fromTypes.id, name: fromTypes.name, description: fromTypes.description };
    }

    const fromOthers = exotics.others?.find(name => name === exoticType);
    if (fromOthers) {
        return { id: fromOthers, name: fromOthers };
    }

    return { id: exoticType, name: exoticType };
};

export const convertToPet = (dto: PetDto, customerId?: number): Pet | null => {
    const masterData = store.getState().masterData.data;
    const marketplace = store.getState().marketplace.data;

    const petType = masterData.petTypes.find(pt => pt.id === dto.pet_type)! as PetType;

    const petSize = marketplace.petTypes[petType.id].sizes.find(ps => ps.id === dto.pet_size);

    if (!petSize) {
        return null;
    }

    const petBreed: PetBreed | undefined =
        petType.id === PetTypeIds.EXOTIC
            ? findExoticBreed(marketplace, dto.exotic_type)
            : masterData.petBreeds.find(
                  breed => breed.id === dto.pet_breed && breed.filter === petType.id
              );

    const petGender = masterData.petGenders.find(g => g.id === dto.pet_gender) as FiltrableOption<
        string,
        PetTypeId
    >;

    const petHairColor = masterData.petHairColors.find(
        hc => hc.id === dto.pet_hair_color && hc.filter === petType.id
    ) as FiltrableOption<string, PetTypeId>;

    const petHairLength = marketplace.petTypes[petType.id].hairLengths.find(
        hl => hl.id === dto.pet_hair_length
    ) as PetHairLength;

    return {
        id: dto.id,
        uuid: dto.uuid || uuid(),
        name: dto.name,

        customerId: customerId ?? dto.owners[0]?.id,

        active: dto.active,
        deleted: dto.deleted,

        type: petType,
        size: petSize,
        breed: petBreed,
        gender: petGender,
        hairColor: petHairColor,
        hairLength: petHairLength,
        weight: dto.pet_weight == null ? undefined : Number(dto.pet_weight),

        birthdate: dto.birthdate ? moment.tz(dto.birthdate, 'Zulu') : null,

        ...(dto.image_url && {
            image: {
                src: dto.image_url
            }
        }),

        vaccines:
            dto.pet_vaccine_expirations?.map(vaccine =>
                convertToPetVaccine(vaccine, marketplace)
            ) ?? [],

        answers: [],

        evaluationReport: dto.report_evaluation
            ? convertToReportEvaluation(dto.report_evaluation)
            : undefined,

        positivePersonalityList: dto.positive_personality_list ?? [],
        negativePersonalityList: dto.negative_personality_list ?? []
    };
};

const convertToPetVaccine = (dto: PetVaccineDto, marketplace: Marketplace): PetVaccine => {
    return {
        id: dto.id,
        uuid: dto.uuid,
        recordUrl: dto.file_url ?? undefined,
        type: Object.values(marketplace.vaccinesByType)
            .flat()
            .find(vaccine => vaccine.id === dto.vaccine_id) as Option<string>,
        expiration: dto.expiration_date
            ? moment(dto.expiration_date).tz(marketplace.timeZone)
            : undefined,
        status: dto.status
    };
};

export const convertToSavePetDto = (pet: SavePet, marketplaceId: number): SavePetDto => {
    const isExotic = pet.type.id === PetTypeIds.EXOTIC;

    return {
        ...(pet.id && { id: pet.id }),
        uuid: pet.uuid,
        marketplace_id: marketplaceId,
        name: pet.name,
        active: pet.active,
        deleted: pet.deleted,
        pet_type: pet.type.id,
        pet_size: pet.size.id,
        pet_weight: pet.weight ?? null,
        pet_breed: !isExotic && typeof pet.breed?.id === 'number' ? pet.breed.id : null,
        exotic_type: isExotic ? String(pet.breed?.id ?? '') || null : null,
        pet_gender: pet.gender?.id ?? null,
        pet_hair_color: pet.hairColor?.id ?? null,
        pet_hair_length: pet.hairLength?.id ?? null,
        birthdate: pet.birthdate ? pet.birthdate.format('YYYY-MM-DD') : null,

        ...(pet.image?.file && { image: pet.image.file }),
        ...(pet.image?.removed && { image: new Blob() }),

        pet_vaccine_expirations_attributes: pet.vaccines.map(vaccine =>
            convertToPetVaccineDto(vaccine)
        ),

        custom_question_responses_attributes: pet.answers?.map(convertToIntakeFormAnswerDto) || [],

        ...(pet.customerId && !pet.id && { owns_attributes: [{ customer_id: pet.customerId }] })
    };
};

export const convertToPetVaccineDto = (vaccine: PetVaccine): SavePetVaccineDto => {
    return {
        id: vaccine.id || null,
        uuid: vaccine.uuid,
        vaccine_id: vaccine.type?.id ?? '',
        expiration_date: vaccine.expiration?.toISOString() || '',

        ...(vaccine.recordFileToUpload && {
            file: vaccine.recordFileToUpload
        })
    };
};
